<template>
    <div class="card mb-25 border-0 rounded-0 bg-white letter-spacing">
        <div
            class="card-head box-shadow bg-white p-15 p-sm-20 p-md-25">
                <div class="row mb-4">
                    <div class="col-sm-6 col-lg-3">
                        <div class="card">
                            <div class="card-body">
                                <div class="d-flex align-items-center">
                                    <div class="subheader">Emprunts en cours</div>
                                    <div class="ms-auto">
                                        <i class="fa fa-clock text-primary fs-4"></i>
                                    </div>
                                </div>
                                <div class="h1 mb-3"></div>
                                <div class="d-flex mb-2">
                                    <div class="flex-fill">
                                        <div class="progress progress-sm">
                                            <div class="progress-bar bg-primary" style="width: 75%" role="progressbar">
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="col-sm-6 col-lg-3">
                        <div class="card">
                            <div class="card-body">
                                <div class="d-flex align-items-center">
                                    <div class="subheader">Emprunts terminés</div>
                                    <div class="ms-auto">
                                        <i class="fa fa-check-circle text-success fs-4"></i>
                                    </div>
                                </div>
                                <div class="h1 mb-3"></div>
                                <div class="d-flex mb-2">
                                    <div class="flex-fill">
                                        <div class="progress progress-sm">
                                            <div class="progress-bar bg-success" style="width: 100%" role="progressbar">
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="col-sm-6 col-lg-3">
                        <div class="card">
                            <div class="card-body">
                                <div class="d-flex align-items-center">
                                    <div class="subheader">En retard</div>
                                    <div class="ms-auto">
                                        <i class="fa fa-exclamation-triangle text-danger fs-4"></i>
                                    </div>
                                </div>
                                <div class="h1 mb-3 text-danger"></div>
                                <div class="d-flex mb-2">
                                    <div class="flex-fill">
                                        <div class="progress progress-sm">
                                            <div class="progress-bar bg-danger" style="width: 60%" role="progressbar">
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="col-sm-6 col-lg-3">
                        <div class="card">
                            <div class="card-body">
                                <div class="d-flex align-items-center">
                                    <div class="subheader">Total emprunts</div>
                                    <div class="ms-auto">
                                        <i class="fa fa-books text-info fs-4"></i>
                                    </div>
                                </div>
                                <div class="h1 mb-3"></div>
                                <div class="d-flex mb-2">
                                    <div class="flex-fill">
                                        <div class="progress progress-sm">
                                            <div class="progress-bar bg-info" style="width: 85%" role="progressbar">
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
        </div>

        <div class="card-body p-15 p-sm-20 p-md-25">
            <!-- Chargement -->
            <div v-if="loading" class="text-center py-4">
                <div class="spinner-border text-primary" role="status">
                    <span class="visually-hidden">Chargement...</span>
                </div>
            </div>

            <!-- Message si aucun résultat -->
            <div v-else-if="documents.length === 0" class="text-center py-4">
                <div class="alert alert-info">
                    <i class="fa fa-info-circle me-2"></i>
                    Aucun emprunt trouvé avec les critères de recherche actuels.
                </div>
            </div>

            <!-- Liste des emprunts en mode tableau -->
            <div v-else class="table-responsive">
                <table class="table table-hover table-striped">
                    <thead class="table-dark">
                        <tr>
                            <th>Image</th>
                            <th>Document</th>
                            <th>Auteur(s)</th>
                            <th>Catégorie</th>
                            <th>Date début</th>
                            <th>Date fin</th>
                            <th>Statut</th>
                            <th>Jours restants</th>
                            <th>Lire</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="emprunt in documents" :key="emprunt.id">
                            <td class="text-center">
                                <img v-if="emprunt.document?.image" 
                                     :src="getImageUrl(emprunt.document.image)" 
                                     :alt="emprunt.document.titre"
                                     class="img-fluid rounded" 
                                     style="width: 50px; height: 50px; object-fit: cover;"
                                     @error="onImageError">
                                <div v-else class="bg-light rounded d-flex align-items-center justify-content-center" 
                                     style="width: 50px; height: 50px;">
                                    <i class="fa fa-book text-muted"></i>
                                </div>
                            </td>
                            <td>
                                <div class="fw-bold">{{ emprunt.document?.titre || 'Titre non disponible' }}</div>
                                <small class="text-muted">{{ emprunt.document?.genre?.nom || 'Non défini' }}</small>
                            </td>
                            <td>{{ emprunt.document?.auteur.nom || 'Auteur non renseigné' }} {{ emprunt.document?.auteur.prenoms || '' }}</td>
                            <td>{{ emprunt.document?.categorie?.nom || 'Non classé' }}</td>
                            <td>{{ formatDate(emprunt.dateDebut) }}</td>
                            <td>{{ formatDate(emprunt.dateFin) }}</td>
                            <td>
                                <span :class="getStatutBadgeClass(emprunt.statutEmprunt)" class="badge">
                                    {{ getStatutLabel(emprunt.statutEmprunt) }}
                                </span>
                            </td>
                            <td>
                                <div v-if="emprunt.statutEmprunt === 'en_cours'">
                                    <span :class="emprunt.joursRestants <= 3 ? 'text-danger fw-bold' : 'text-success'">
                                        <i class="fa fa-clock me-1"></i>
                                        {{ emprunt.joursRestants > 0 ? 
                                            `${emprunt.joursRestants} jour(s)` : 
                                            'Expire aujourd\'hui' 
                                        }}
                                    </span>
                                </div>
                                <div v-else-if="emprunt.statutEmprunt === 'en_retard'" class="text-danger fw-bold">
                                    <i class="fa fa-exclamation-triangle me-1"></i>
                                    {{ Math.abs(emprunt.joursRestants) }} jour(s) de retard
                                </div>
                                <div v-else-if="emprunt.statutEmprunt === 'termine'" class="text-success">
                                    <i class="fa fa-check-circle me-1"></i>
                                    Terminé
                                </div>
                            </td>
                            <td class="text-center">
                                <button v-if="emprunt.statutEmprunt === 'en_cours'"
                                    class="btn btn-success btn-sm" 
                                    @click="lireDocument(emprunt)"
                                    title="Lire le document">
                                    <i class="fa fa-book-open me-1"></i>
                                    Lire
                                </button>
                                <span v-else class="text-muted">
                                    <i class="fa fa-lock me-1"></i>
                                    Non disponible
                                </span>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <!-- Pagination -->
            <nav v-if="totalPages > 1" aria-label="Navigation des pages" class="mt-4">
                <ul class="pagination justify-content-center">
                    <li class="page-item" :class="{ disabled: currentPage <= 1 }">
                        <button class="page-link" @click="changerPage(currentPage - 1)" :disabled="currentPage <= 1">
                            Précédent
                        </button>
                    </li>

                    <li v-for="page in getPagesArray()" :key="page" class="page-item"
                        :class="{ active: page === currentPage }">
                        <button class="page-link" @click="changerPage(page)">{{ page }}</button>
                    </li>

                    <li class="page-item" :class="{ disabled: currentPage >= totalPages }">
                        <button class="page-link" @click="changerPage(currentPage + 1)"
                            :disabled="currentPage >= totalPages">
                            Suivant
                        </button>
                    </li>
                </ul>

                <div class="text-center text-muted">
                    Page {{ currentPage }} sur {{ totalPages }} - {{ totalElements }} résultat(s) au total
                </div>
            </nav>
        </div>

        <!-- Modal des détails -->
        <div v-if="showModalDetails" class="modal fade show d-block" tabindex="-1"
            style="background-color: rgba(0,0,0,0.5);">
            <div class="modal-dialog modal-lg">
                <div class="modal-content">
                    <div class="modal-header">
                        <h5 class="modal-title">
                            <i class="fa fa-book me-2"></i>
                            Détails de l'emprunt
                        </h5>
                        <button type="button" class="btn-close" @click="fermerModalDetails()"></button>
                    </div>

                    <div v-if="empruntSelectionne" class="modal-body">
                        <!-- Informations du document -->
                        <div class="card mb-3">
                            <div class="card-header bg-primary text-white">
                                <h6 class="mb-0">
                                    <i class="fa fa-book me-2"></i>
                                    Informations du document
                                </h6>
                            </div>
                            <div class="card-body">
                                <div class="row">
                                    <div class="col-md-8">
                                        <h5>{{ empruntSelectionne.document?.titre || 'Titre non disponible' }}</h5>
                                        <p class="text-muted mb-2">
                                            <strong>Auteur(s):</strong>
                                            {{ getAuteursNoms(empruntSelectionne.document?.auteurs) }}
                                        </p>
                                        <p class="text-muted mb-2">
                                            <strong>Catégorie:</strong>
                                            {{ empruntSelectionne.document?.categorie?.nom || 'Non classé' }}
                                        </p>
                                        <p class="text-muted mb-2">
                                            <strong>Genre:</strong>
                                            {{ empruntSelectionne.document?.genre?.nom || 'Non défini' }}
                                        </p>
                                        <p class="text-muted">
                                            <strong>Maison d'édition:</strong>
                                            {{ empruntSelectionne.document?.maisonEdition?.nom || 'Non renseigné' }}
                                        </p>
                                    </div>
                                    <div v-if="empruntSelectionne.document?.image" class="col-md-4 text-center">
                                        <img :src="getImageUrl(empruntSelectionne.document.image)"
                                            :alt="empruntSelectionne.document.titre" class="img-fluid rounded shadow"
                                            style="max-height: 200px; object-fit: cover;" @error="onImageError">
                                    </div>
                                </div>
                            </div>
                        </div>

                        <!-- Informations de l'emprunt -->
                        <div class="card mb-3">
                            <div class="card-header bg-info text-white">
                                <h6 class="mb-0">
                                    <i class="fa fa-calendar me-2"></i>
                                    Informations de l'emprunt
                                </h6>
                            </div>
                            <div class="card-body">
                                <div class="row">
                                    <div class="col-md-6">
                                        <div class="mb-3">
                                            <strong class="text-muted">Date de début:</strong>
                                            <div class="fs-5 text-primary">{{ formatDate(empruntSelectionne.dateDebut)
                                                }}</div>
                                        </div>
                                        <div class="mb-3">
                                            <strong class="text-muted">Date de fin:</strong>
                                            <div class="fs-5 text-primary">{{ formatDate(empruntSelectionne.dateFin) }}
                                            </div>
                                        </div>
                                        <div class="mb-3">
                                            <strong class="text-muted">Statut:</strong>
                                            <div>
                                                <span :class="getStatutBadgeClass(empruntSelectionne.statutEmprunt)"
                                                    class="badge fs-6">
                                                    {{ getStatutLabel(empruntSelectionne.statutEmprunt) }}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                    <div class="col-md-6">
                                        <div class="mb-3">
                                            <strong class="text-muted">Date de demande:</strong>
                                            <div class="fs-6">{{ formatDate(empruntSelectionne.createdAt) }}</div>
                                        </div>
                                        <div v-if="empruntSelectionne.motif" class="mb-3">
                                            <strong class="text-muted">Motif de l'emprunt:</strong>
                                            <div class="fs-6">{{ empruntSelectionne.motif }}</div>
                                        </div>
                                        <div v-if="empruntSelectionne.statutEmprunt === 'en_cours'" class="mb-3">
                                            <strong class="text-muted">Jours restants:</strong>
                                            <div class="fs-6">
                                                <span
                                                    :class="empruntSelectionne.joursRestants <= 3 ? 'text-danger' : 'text-success'">
                                                    {{ empruntSelectionne.joursRestants }} jour(s)
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <!-- Actions disponibles -->
                        <div class="card">
                            <div class="card-header bg-success text-white">
                                <h6 class="mb-0">
                                    <i class="fa fa-cogs me-2"></i>
                                    Actions disponibles
                                </h6>
                            </div>
                            <div class="card-body">
                                <div class="d-flex gap-2 flex-wrap">
                                    <button v-if="empruntSelectionne.statutEmprunt === 'en_cours'"
                                        class="btn btn-success" @click="lireDocument(empruntSelectionne)">
                                        <i class="fa fa-book-open me-1"></i>
                                        Lire le document
                                    </button>

                                    <button v-if="empruntSelectionne.statutEmprunt === 'en_cours'"
                                        class="btn btn-outline-primary" @click="prolongerEmprunt(empruntSelectionne)">
                                        <i class="fa fa-calendar-plus me-1"></i>
                                        Demander une prolongation
                                    </button>

                                    <button class="btn btn-outline-info" @click="voirHistorique(empruntSelectionne)">
                                        <i class="fa fa-history me-1"></i>
                                        Voir l'historique
                                    </button>

                                    <button class="btn btn-outline-warning"
                                        @click="signalerProbleme(empruntSelectionne)">
                                        <i class="fa fa-exclamation-triangle me-1"></i>
                                        Signaler un problème
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="modal-footer">
                        <button type="button" class="btn btn-secondary" @click="fermerModalDetails()">
                            Fermer
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script lang="ts">
import { defineComponent, ref, reactive, onMounted } from 'vue';
import ApiService from "@/services/ApiService";
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { useRouter } from 'vue-router';

export default defineComponent({
    name: 'MesEmprunts',
    emits: ['statistiques-updated'],

    setup(_, { emit }) {
        const router = useRouter();

        // Reactive data
        const documents = ref<any[]>([]);
        const loading = ref<boolean>(false);
        const searchTerm = ref<string>('');
        const filtres = reactive({
            statutEmprunt: '',
            dateDebutDeclaration: '',
            dateFinDeclaration: ''
        });

        // Pagination
        const currentPage = ref<number>(1);
        const totalPages = ref<number>(0);
        const totalElements = ref<number>(0);
        const limit = ref<number>(12);

        // Messages d'erreur
        const errorMessage = ref<string>('');

        // Modal des détails
        const showModalDetails = ref<boolean>(false);
        const empruntSelectionne = ref<any>(null);
        // Methods
        const chargerDocuments = async () => {
            loading.value = true;
            errorMessage.value = '';

            try {
                const params = {
                    page: currentPage.value,
                    limit: limit.value,
                    searchTerm: searchTerm.value || undefined,
                    ...filtres
                };

                // Nettoyer les paramètres vides
                Object.keys(params).forEach(key => {
                    if (params[key] === '' || params[key] === null || params[key] === undefined) {
                        delete params[key];
                    }
                });

                const response = await ApiService.query('/emprunts/user/documents', params);

                if (response.data) {
                    console.log(response.data,"eeeeeeeeeeeeeeeeeeeeeee");
                    documents.value = response.data.data.data || [];
                    totalPages.value = response.data.data.totalPages || 0;
                    totalElements.value = response.data.data.totalElements || 0;
                    currentPage.value = response.data.data.currentPage || 1;

                    // Émettre les statistiques vers le parent
                    emit('statistiques-updated', documents.value);
                } else {
                    throw new Error(response.data.message || 'Erreur lors du chargement des données');
                }

            } catch (error: any) {
                console.error('Erreur lors du chargement des emprunts:', error);
                errorMessage.value = error.response?.data?.message || 'Erreur lors du chargement des emprunts';
                documents.value = [];
            } finally {
                loading.value = false;
            }
        };

        const rechercher = () => {
            currentPage.value = 1;
            chargerDocuments();
        };

        const reinitialiserFiltres = () => {
            searchTerm.value = '';
            filtres.statutEmprunt = '';
            filtres.dateDebutDeclaration = '';
            filtres.dateFinDeclaration = '';
            currentPage.value = 1;
            chargerDocuments();
        };

        const changerPage = (page: number) => {
            if (page >= 1 && page <= totalPages.value && page !== currentPage.value) {
                currentPage.value = page;
                chargerDocuments();
            }
        };

        const getPagesArray = (): number[] => {
            const pages: number[] = [];
            const startPage = Math.max(1, currentPage.value - 2);
            const endPage = Math.min(totalPages.value, currentPage.value + 2);

            for (let i = startPage; i <= endPage; i++) {
                pages.push(i);
            }

            return pages;
        };

        const getStatutBadgeClass = (statut: string): string => {
            const classes: Record<string, string> = {
                'en_cours': 'bg-primary',
                'termine': 'bg-success',
                'en_retard': 'bg-danger'
            };
            return classes[statut] || 'bg-secondary';
        };

        const getStatutLabel = (statut: string): string => {
            const labels: Record<string, string> = {
                'en_cours': 'En cours',
                'termine': 'Terminé',
                'en_retard': 'En retard'
            };
            return labels[statut] || statut;
        };

        const formatDate = (dateString: string | null): string => {
            if (!dateString) return 'Date non disponible';
            try {
                return format(new Date(dateString), 'dd/MM/yyyy', { locale: fr });
            } catch (error) {
                console.error('Erreur de formatage de date:', error);
                return 'Date invalide';
            }
        };

        const getAuteursNoms = (auteurs: any[]): string => {
            if (!auteurs || !Array.isArray(auteurs) || auteurs.length === 0) {
                return 'Auteur non renseigné';
            }

            return auteurs.map(auteur => `${auteur.prenom || ''} ${auteur.nom || ''}`.trim()).join(', ');
        };

        const getImageUrl = (imagePath: string | null): string => {
            if (!imagePath) return '/no-image.svg';

            // Si c'est déjà une URL complète
            if (imagePath.startsWith('http')) {
                return imagePath;
            }

            // Construire l'URL avec le serveur
            const baseUrl = `${process.env.VUE_APP_BASEURL}/`;
            return `${baseUrl}${imagePath.startsWith('/') ? imagePath.slice(1) : imagePath}`;
        };

        const onImageError = (event: Event) => {
            // En cas d'erreur de chargement d'image, afficher une image par défaut
            const target = event.target as HTMLImageElement;
            target.src = '/no-image.svg';
        };
        const prolongerEmprunt = async (emprunt: any) => {
            try {
                // Demander une nouvelle date de fin
                const nouvelleDateFin = prompt('Nouvelle date de fin souhaitée (YYYY-MM-DD):', '');

                if (!nouvelleDateFin) return;

                // Demander une justification
                const justification = prompt('Justification pour la prolongation:', '');

                if (!justification) return;

                const confirmation = confirm(`Voulez-vous demander une prolongation pour "${emprunt.document?.titre}" jusqu'au ${nouvelleDateFin} ?`);

                if (confirmation) {
                    const response = await ApiService.post(`/emprunts/${emprunt.id}/prolongation`, {
                        nouvelleDateFin: nouvelleDateFin,
                        justification: justification
                    });

                    if (response.data.success) {
                        alert('Demande de prolongation envoyée avec succès ! Vous recevrez une réponse sous peu.');
                        // Recharger les données pour voir les changements
                        chargerDocuments();
                    } else {
                        throw new Error(response.data.message || 'Erreur lors de l\'envoi de la demande');
                    }
                }
            } catch (error: any) {
                console.error('Erreur lors de la demande de prolongation:', error);
                const message = error.response?.data?.message || 'Erreur lors de l\'envoi de la demande de prolongation';
                alert(message);
            }
        };

        const voirDetails = async (emprunt: any) => {
            try {
                // Charger les détails complets de l'emprunt
                const response = await ApiService.get(`/emprunts/${emprunt.id}`);

                if (response.data.success) {
                    empruntSelectionne.value = response.data.data;
                    showModalDetails.value = true;
                } else {
                    throw new Error(response.data.message || 'Erreur lors du chargement des détails');
                }
            } catch (error) {
                console.error('Erreur lors du chargement des détails:', error);
                alert('Erreur lors du chargement des détails de l\'emprunt');
            }
        };

        const fermerModalDetails = () => {
            showModalDetails.value = false;
            empruntSelectionne.value = null;
        };

        const voirHistorique = async (emprunt: any) => {
            try {
                const response = await ApiService.get(`/emprunts/${emprunt.id}/historique`);

                if (response.data.success) {
                    const historique = response.data.data;
                    let message = "Historique de l'emprunt:\n\n";

                    if (historique && historique.length > 0) {
                        historique.forEach((action: any) => {
                            message += `• ${formatDate(action.date)} - ${action.action}\n`;
                        });
                    } else {
                        message += "Aucun historique disponible.";
                    }

                    alert(message);
                } else {
                    throw new Error(response.data.message || 'Erreur lors du chargement de l\'historique');
                }
            } catch (error) {
                console.error('Erreur lors du chargement de l\'historique:', error);
                alert('Erreur lors du chargement de l\'historique de l\'emprunt');
            }
        };

        const signalerProbleme = async (emprunt: any) => {
            const probleme = prompt(`Décrivez le problème rencontré avec "${emprunt.document?.titre}":`, '');

            if (!probleme) return;

            try {
                const response = await ApiService.post(`/emprunts/${emprunt.id}/probleme`, {
                    probleme: probleme
                });

                if (response.data.success) {
                    alert('Problème signalé avec succès ! L\'équipe de la bibliothèque vous contactera sous peu.');
                    fermerModalDetails();
                } else {
                    throw new Error(response.data.message || 'Erreur lors du signalement');
                }
            } catch (error: any) {
                console.error('Erreur lors du signalement du problème:', error);
                const message = error.response?.data?.message || 'Erreur lors du signalement du problème';
                alert(message);
            }
        };

        const lireDocument = async (emprunt: any) => {
            if (!emprunt.document) {
                alert('Document non disponible');
                return;
            }

            try {
                // Enregistrer la lecture dans l'historique
                await ApiService.post('/historiques', {
                    document: emprunt.document.id,
                    types: 'LECTURE',
                    action: `Lecture du document "${emprunt.document.titre}"`,
                });

                // Rediriger vers le lecteur PDF
                if (emprunt.document.nomFichier) {
                    router.push({
                        name: 'pdf-viewer',
                        params: { id: emprunt.document.id },
                        query: { 
                            pdfUrl: `${ApiService.vueInstance.axios.defaults.baseURL?.split("api")[0]}uploads/${emprunt.document.nomFichier}` 
                        }
                    });
                } else {
                    alert('Fichier PDF non disponible pour ce document');
                }
            } catch (error: any) {
                console.error('Erreur lors de l\'ouverture du document:', error);
                const message = error.response?.data?.message || 'Erreur lors de l\'ouverture du document';
                alert(message);
            }
        };

        // Lifecycle
        onMounted(() => {
            chargerDocuments();
        });

        // Return all reactive data and methods for template usage
        return {
            // Reactive data
            documents,
            loading,
            searchTerm,
            filtres,
            currentPage,
            totalPages,
            totalElements,
            limit,
            errorMessage,
            showModalDetails,
            empruntSelectionne,

            // Methods
            chargerDocuments,
            rechercher,
            reinitialiserFiltres,
            changerPage,
            getPagesArray,
            getStatutBadgeClass,
            getStatutLabel,
            formatDate,
            getAuteursNoms,
            getImageUrl,
            onImageError,
            prolongerEmprunt,
            voirDetails,
            fermerModalDetails,
            voirHistorique,
            signalerProbleme,
            lireDocument
        };
    }
});
</script>

<style scoped>
.card {
    transition: transform 0.2s ease-in-out;
}

.card:hover {
    transform: translateY(-2px);
}

.badge {
    font-size: 0.75rem;
}

.alert {
    border: none;
}

.pagination .page-link {
    color: #6c757d;
    border-color: #dee2e6;
}

.pagination .page-item.active .page-link {
    background-color: #007bff;
    border-color: #007bff;
}

.btn-group .btn {
    margin: 0 2px;
}

.form-select,
.form-control {
    border: 1px solid #dee2e6;
}

.form-select:focus,
.form-control:focus {
    border-color: #80bdff;
    box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
}

/* Modal styles */
.modal.fade.show {
    animation: fadeIn 0.3s ease-in-out;
}

@keyframes fadeIn {
    from {
        opacity: 0;
    }

    to {
        opacity: 1;
    }
}

.modal-dialog {
    margin: 1.75rem auto;
}

.modal-content {
    border: none;
    border-radius: 0.5rem;
    box-shadow: 0 1rem 3rem rgba(0, 0, 0, 0.175);
}

.modal-header {
    border-bottom: 1px solid #dee2e6;
    padding: 1rem 1.5rem;
}

.modal-body {
    padding: 1.5rem;
}

.modal-footer {
    border-top: 1px solid #dee2e6;
    padding: 1rem 1.5rem;
}

/* Card enhancements */
.card-header {
    font-weight: 600;
}

.badge {
    font-weight: 500;
    letter-spacing: 0.025em;
}

/* Image styles */
.img-fluid {
    border-radius: 0.375rem;
}

/* Button enhancements */
.btn-group .btn {
    border-radius: 0.25rem;
}

.btn-outline-primary:hover,
.btn-outline-info:hover,
.btn-outline-warning:hover {
    transform: translateY(-1px);
    box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.075);
}

/* Alert enhancements */
.alert {
    border-radius: 0.375rem;
}

.alert-info {
    background-color: #d1ecf1;
    border-color: #bee5eb;
    color: #0c5460;
}

.alert-danger {
    background-color: #f8d7da;
    border-color: #f5c6cb;
    color: #721c24;
}

.alert-success {
    background-color: #d4edda;
    border-color: #c3e6cb;
    color: #155724;
}

/* Table enhancements */
.table-responsive {
    border-radius: 0.375rem;
    overflow: hidden;
    box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.075);
}

.table {
    margin-bottom: 0;
}

.table th {
    border-top: none;
    font-weight: 600;
    font-size: 0.875rem;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    padding: 1rem 0.75rem;
}

.table td {
    padding: 1rem 0.75rem;
    vertical-align: middle;
    border-top: 1px solid #dee2e6;
}

.table tbody tr:hover {
    background-color: rgba(0, 123, 255, 0.05);
    transform: translateY(-1px);
    transition: all 0.2s ease-in-out;
}

.table .badge {
    font-size: 0.75rem;
    padding: 0.375em 0.75em;
}

/* Action buttons in table */
.table .btn-group-sm .btn {
    padding: 0.25rem 0.5rem;
    font-size: 0.75rem;
}

.table .btn-group .btn:not(:last-child) {
    margin-right: 0.25rem;
}

/* Responsive table improvements */
@media (max-width: 768px) {
    .table-responsive table {
        font-size: 0.875rem;
    }
    
    .table th,
    .table td {
        padding: 0.5rem 0.25rem;
    }
    
    .table .btn-group {
        flex-direction: column;
    }
    
    .table .btn-group .btn {
        margin-bottom: 0.25rem;
        margin-right: 0;
    }
}

.page-header {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  padding: 2rem 0;
  margin-bottom: 2rem;
}

.page-title {
  color: white;
  margin-bottom: 0;
}

.page-pretitle {
  color: rgba(255, 255, 255, 0.8);
  font-size: 0.875rem;
  font-weight: 500;
  margin-bottom: 0.25rem;
}

.card {
  border: none;
  box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.075);
  transition: all 0.15s ease-in-out;
}

.card:hover {
  box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.15);
  transform: translateY(-2px);
}

.subheader {
  color: #6c757d;
  font-size: 0.875rem;
  font-weight: 500;
}

.h1 {
  font-size: 2rem;
  font-weight: 700;
  line-height: 1.2;
}

.progress {
  height: 0.375rem;
}

.alert-title {
  font-size: 1.125rem;
  font-weight: 600;
  margin-bottom: 0.5rem;
}

.btn-outline-primary {
  border: 2px solid rgba(255, 255, 255, 0.3);
  color: white;
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
}

.btn-outline-primary:hover {
  background: rgba(255, 255, 255, 0.2);
  border-color: rgba(255, 255, 255, 0.5);
  color: white;
}

@media (max-width: 768px) {
  .page-header {
    padding: 1rem 0;
  }
  
  .h1 {
    font-size: 1.5rem;
  }
  
  .btn-list {
    margin-top: 1rem;
  }
}
</style>

